import React, { useState, useEffect, useRef } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  AppLayout,
  BreadcrumbGroup,
  Button,
  Cards,
  ColumnLayout,
  Container,
  Flashbar,
  FormField,
  Icon,
  Input,
  SpaceBetween,
  Textarea,
  Header,
  Alert,
  Grid,
  Modal,
  Spinner,
  Box,
  Link,
  Tabs,
  ExpandableSection,
  Table,
  Select,
  Pagination,
  Popover,
  FileUpload,
  Multiselect
} from "@amzn/awsui-components-react";

// Shared utilities from TemplateOak pattern
import { addAIApp, updateAIApp, getAIApp, deleteAppWithApproval, createAuditLog, getApprovalsForApp } from "./Utils/AppActivityUtilsAppsyncPlus";
import { getSupervisorAtLevel } from "./Utils/AppActivityUtils";
import { getLogsById } from "../AuditLogging/AuditLogsAPIs";
import { getUserRatings, submitRating, deleteRating } from "src/utilities/RatingAPIs";
import StarRating from "src/Home/StarRating";
import { canAddEditDeleteApplication } from "./Utils/Permissions";
import { isMemberOf } from "src/checkPageAccess";
import { getEditableFields } from "./Utils/FormMetadata";
import { favorite, checkIsFavorite } from "../../utilities/Favorites";
import { featured, checkIsFeatured } from "../../utilities/Featured";
import { startWASApproval } from "../../utilities/ApprovalAPIs";
import { updateAutomatedApp } from "../../utilities/AutomatedAppsAPIs";
import { TEMPLATES, TEMPLATE_APP, AI_APP_SPECIFIC_FIELDS, TEMPLATE_APP_SOURCE_DATA_TABLES } from "./Constants";
import { GET_APPS_CACHE_KEY } from "../../AppConstants/CacheConstants";
import { setPendingTrigger, ActionTriggerType } from "../../store/pulseSurveyTriggerSlice";

// Custom input components
import CustomSelect from "./components/CustomSelect";
import CustomMultiSelect from "./components/CustomMultiSelect";
import CustomInput from "./components/CustomInput";
import CustomSelectAlias from "./components/CustomSelectAlias";
import CustomMultiHybridInputsForSourceTables from "./components/CustomMultiHybridInputsForSourceTables";
import { useSuggestedLogins, useDirectReports } from "src/hooks/useSuggestedLogins";
import ReactMarkdown from "react-markdown";
import SkeletonCard from "../../Home/SkeletonCard";
import AddToCPModal from "src/Home/ClosePortal/components/AddToCPModal";
import HomeSideNav from "src/Home/SideNav";
import { uploadDocumentToVault, getDocumentFromVault, removeDocumentFromVault } from "src/Ateam-BI/Vault/VaultAPIs";
import { VAULT_SERVICES } from "src/Ateam-BI/Vault/constants";
import BuildingWithAIModal from "./BuildingWithAIModal";

function AIAppPage(props) {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { id: routeId } = useParams();
  const currentStage = useSelector((globalState) => globalState.auth.currentStage);
  const userId = useSelector((globalState) => globalState.auth.userId);

  // Core state - renderJson/renderJsonEdited dual-state pattern (from TemplateOak)
  const [renderJson, setRenderJson] = useState(props?.renderJson || null);
  const [renderJsonEdited, setRenderJsonEdited] = useState(props?.renderJson || null);
  const [isEditMode, setIsEditMode] = useState(props?.isEditOverride || false);
  const [isNewApp, setIsNewApp] = useState(false);
  const [id, setId] = useState(props?.id || routeId || null);

  // Permission state
  const [canDelete, setCanDelete] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canAdd, setCanAdd] = useState(false);
  const [isAiAdminLocal, setIsAiAdminLocal] = useState(false);

  // Field metadata
  const [fieldMetadata, setFieldMetadata] = useState({});

  // UI state
  const [showLoadingIcon, setShowLoadingIcon] = useState(!props?.renderJson);
  const [errorMessage, setErrorMessage] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [showSaveSuccessModal, setShowSaveSuccessModal] = useState(false);
  const savedAppDataRef = useRef(null);
  const [alertErrorMessage, setAlertErrorMessage] = useState(null);
  const [updateComments, setUpdateComments] = useState("");
  const [modalError, setModalError] = useState("");

  // Favorites/Featured state
  const [isFavorite, setIsFavorite] = useState(false);
  const [favDisabled, setFavDisabled] = useState(false);
  const [featuredPressed, setFeaturedPressed] = useState(false);

  // Delete confirmation state
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);
  const [deleteAppName, setDeleteAppName] = useState("");

  // PAPI banner state (non-prod unpublished queue warning)
  const [papiWarningDismissed, setPapiWarningDismissed] = useState(false);

  // Audit/Change log state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  const [changeLogPage, setChangeLogPage] = useState(1);
  const CHANGE_LOG_PAGE_SIZE = 5;

  // Close Portal modal state
  const [addToCPModalVisible, setAddToCPModalVisible] = useState(false);

  // Document upload state
  const [appDocuments, setAppDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);

  // User rating state
  const [userRating, setUserRating] = useState(0);
  const [displayAvgRating, setDisplayAvgRating] = useState(0);
  const [displayRatingCount, setDisplayRatingCount] = useState(0);

  // Alias lookup state (for CustomSelectAlias) - uses useSuggestedLogins hook
  const { suggestedLogins, aliasesLoading, getSuggestedLoginsCaller } = useSuggestedLogins();

  // VP direct reports (same pattern as CreateTeamModal)
  const { directReports, directReportsLoading, getDirectReportsCaller } = useDirectReports();

  // --- Helper Functions (TemplateOak pattern) ---

  const getValue = (fieldName, subKey = null) => {
    if (subKey) {
      if (renderJsonEdited?.hasOwnProperty(fieldName)) {
        return renderJsonEdited[fieldName][subKey];
      } else {
        return null;
      }
    } else {
      return renderJsonEdited ? renderJsonEdited[fieldName] : null;
    }
  };

  const updateValue = (fieldName, value, subKey = null) => {
    let clone = JSON.parse(JSON.stringify(renderJsonEdited || {}));
    if (subKey) {
      if (!clone.hasOwnProperty(fieldName)) {
        clone[fieldName] = {};
      }
      clone[fieldName][subKey] = value;
    } else {
      clone[fieldName] = value;
    }
    setRenderJsonEdited(clone);
  };

  const isEditable = (fieldName, subKey = null) => {
    if (subKey) {
      return isNewApp || (isEditMode && fieldMetadata && fieldMetadata[fieldName] && fieldMetadata[fieldName][subKey] && fieldMetadata[fieldName][subKey]['editable']);
    } else {
      return isNewApp || (isEditMode && fieldMetadata && fieldMetadata[fieldName] && fieldMetadata[fieldName]['editable']);
    }
  };

  const getSelectFieldValues = (fieldName, subKey = null) => {
    if (subKey && fieldMetadata && fieldMetadata[fieldName] && fieldMetadata[fieldName][subKey] && fieldMetadata[fieldName][subKey]['values']) {
      return fieldMetadata[fieldName][subKey]['values'];
    } else if (fieldMetadata && fieldMetadata[fieldName] && fieldMetadata[fieldName]['values']) {
      return fieldMetadata[fieldName]['values'];
    } else {
      return [];
    }
  };

  const getMultiSelectedOptions = (fieldName) => {
    let arr = renderJsonEdited ? renderJsonEdited[fieldName] : null;
    let selectedOptions = [];
    if (arr) {
      arr.forEach((item) => {
        selectedOptions.push({ label: item, value: item });
      });
    }
    return selectedOptions;
  };

  // --- Display Helpers for View Mode ---

  const requiredLabel = (text) => <>{text} <span style={{color: "red"}}>*</span></>;

  const displayValue = (fieldName) => {
    const val = getValue(fieldName);
    if (val == null || val === "") return "—";
    if (Array.isArray(val)) {
      return val.length > 0 ? val.join(", ") : "—";
    }
    return val;
  };

  const displayLink = (fieldName) => {
    let val = getValue(fieldName);
    if (val == null || val === "") return "—";
    if (Array.isArray(val)) {
      val = val.length > 0 ? val[0] : null;
      if (val == null || val === "") return "—";
    }
    return <Link href={val} external>{val}</Link>;
  };

  const displayLabeledLink = (fieldName, label) => {
    let val = getValue(fieldName);
    if (val == null || val === "") return "—";
    if (Array.isArray(val)) {
      val = val.length > 0 ? val[0] : null;
      if (val == null || val === "") return "—";
    }
    return <Link href={val} external>{label}</Link>;
  };

  const displayAlias = (fieldName) => {
    let val = getValue(fieldName);
    if (val == null || val === "") return "—";
    if (Array.isArray(val)) {
      return val.filter(v => v).map((alias, i) => (
        <span key={i}>{i > 0 && ", "}<Link href={`https://phonetool.amazon.com/users/${alias}`} target="_blank">{alias}</Link></span>
      ));
    }
    return <Link href={`https://phonetool.amazon.com/users/${val}`} target="_blank">{val}</Link>;
  };

  // --- Conditional field helpers ---

  const isSoxInScope = () => {
    const val = getValue(AI_APP_SPECIFIC_FIELDS.SOX_IMPACT);
    if (Array.isArray(val)) {
      return val.includes("In Scope");
    }
    return val === "In Scope";
  };

  const generateEmbedableBroadcastLinkFromVideoLink = (url) => {
    let videoId = "";
    let embedableLink = "https://broadcast.amazon.com/embed/";
    let urlSplit = url?.replace(/\/$/, '').split("/");
    let foundVideosIndex = urlSplit?.findIndex((e) => e === 'videos');
    if (foundVideosIndex !== -1 && urlSplit) {
      videoId = urlSplit[foundVideosIndex + 1];
    }
    if (videoId && videoId !== "") {
      return `${embedableLink}${videoId}`;
    }
    return embedableLink;
  };

  const getDemoVideoUrl = () => {
    let val = getValue(AI_APP_SPECIFIC_FIELDS.DEMO_VIDEO_URL);
    if (Array.isArray(val)) {
      val = val.length > 0 ? val[0] : null;
    }
    return val || "";
  };

  // --- Edit Mode Functions (TemplateOak pattern) ---

  const cancelEdition = () => {
    setRenderJsonEdited(JSON.parse(JSON.stringify(renderJson)));
    setErrorMessage("");
    setIsEditMode(false);
    setUpdateComments("");
    // Reset documents to saved state (remove pending uploads, restore deleted items)
    setAppDocuments(prev => prev.filter(d => !d.pending).map(({ markedForDeletion, ...rest }) => rest));
  };

  const editApplication = () => {
    setIsEditMode(true);
    setRenderJsonEdited(JSON.parse(JSON.stringify(renderJson)));
    setErrorMessage("");
  };

  // --- Mandatory Field Validation (Task 6.1) ---

  const AI_APP_MANDATORY_FIELDS = [
    TEMPLATE_APP.APP_NAME,
    TEMPLATE_APP.APP_DESCRIPTION,
    AI_APP_SPECIFIC_FIELDS.USE_CASE_TYPE,
    AI_APP_SPECIFIC_FIELDS.SOLUTION_TYPE,
    TEMPLATE_APP.ORGANIZATION,
    AI_APP_SPECIFIC_FIELDS.AUTOMATION_TOOL,
    AI_APP_SPECIFIC_FIELDS.APP_OWNER,
    AI_APP_SPECIFIC_FIELDS.DIRECTOR,
    TEMPLATE_APP.GRC_CONTROL_OWNER,
    TEMPLATE_APP.VP,
    TEMPLATE_APP.REGION,
    TEMPLATE_APP.ANNUAL_MER_SAVINGS,
    AI_APP_SPECIFIC_FIELDS.SOX_IMPACT,
    AI_APP_SPECIFIC_FIELDS.APPLICATION_LINK,
    AI_APP_SPECIFIC_FIELDS.OVERVIEW_CONTENT,
  ];

  const FIELD_LABELS = {
    [TEMPLATE_APP.APP_NAME]: "Application Name",
    [TEMPLATE_APP.APP_DESCRIPTION]: "Description",
    [AI_APP_SPECIFIC_FIELDS.USE_CASE_TYPE]: "Use Case Type",
    [AI_APP_SPECIFIC_FIELDS.TAGS]: "Tags",
    [AI_APP_SPECIFIC_FIELDS.SOLUTION_TYPE]: "Solution Type",
    [TEMPLATE_APP.ORGANIZATION]: "Organization",
    [AI_APP_SPECIFIC_FIELDS.AUTOMATION_TOOL]: "Automation Tool",
    [AI_APP_SPECIFIC_FIELDS.APP_OWNER]: "App Builder",
    [AI_APP_SPECIFIC_FIELDS.DIRECTOR]: "Director (L8)",
    [TEMPLATE_APP.GRC_CONTROL_OWNER]: "GRC Control Owner",
    [TEMPLATE_APP.VP]: "VP",
    [TEMPLATE_APP.REGION]: "Region",
    [TEMPLATE_APP.ANNUAL_MER_SAVINGS]: "Estimated Annual MER (hours)",
    [AI_APP_SPECIFIC_FIELDS.SOX_IMPACT]: "SOX Impact",
    [AI_APP_SPECIFIC_FIELDS.APPLICATION_LINK]: "Solution URL",
    [AI_APP_SPECIFIC_FIELDS.GRC_LINK]: "Link to GRC",
    [AI_APP_SPECIFIC_FIELDS.OVERVIEW_CONTENT]: "Overview Content",
    [AI_APP_SPECIFIC_FIELDS.SUPPORT_CONTACT_NAME]: "Point of Contact",
    [AI_APP_SPECIFIC_FIELDS.SUPPORT_EMAIL]: "Contact Email",
  };

  const getMissingFields = (data, fields) => {
    let missing = [];
    // Add GRC_LINK as mandatory if SOX Impact is "In Scope"
    let allFields = [...fields];
    const soxVal = data?.[AI_APP_SPECIFIC_FIELDS.SOX_IMPACT];
    const soxIsInScope = Array.isArray(soxVal) ? soxVal.includes("In Scope") : soxVal === "In Scope";
    if (soxIsInScope && !allFields.includes(AI_APP_SPECIFIC_FIELDS.GRC_LINK)) {
      allFields.push(AI_APP_SPECIFIC_FIELDS.GRC_LINK);
    }
    allFields.forEach((field) => {
      const val = data?.[field];
      if (val == null || val === "" || (Array.isArray(val) && val.length === 0)) {
        missing.push(FIELD_LABELS[field] || field);
      }
    });
    return missing;
  };

  // --- CRUD Operations (Tasks 5.1, 5.2, 5.3) ---

  // Convert array values to strings for single-select fields before sending to API
  const normalizeAppDataForMutation = (appData) => {
    // Allowlist: only keep fields that are in AddAIAppInput/UpdateAIAppInput schema
    const allowedFields = [
      "app_id", "template_id", "app_name", "app_description", "status",
      "annual_mer_savings", "pdd", "created_time_stamp", "last_updated_time_stamp",
      "created_by", "last_updated_by", "organization", "ai_use_case_type",
      "region", "vp", "director", "grc_control_owner", "app_owning_ldap", "ai_automation_tool",
      "tags", "solution_type", "app_owner", "permissions_link", "sox_impact",
      "grc_link", "application_link", "overview_content", "demo_video_url",
      "sop_url", "support_contact_name", "support_email", "source_data_tables",
      "documents",
    ];
    Object.keys(appData).forEach((key) => {
      if (!allowedFields.includes(key)) {
        delete appData[key];
      }
    });

    // Convert array values to strings for single-select fields
    const singleValueFields = [
      AI_APP_SPECIFIC_FIELDS.SOLUTION_TYPE,
      AI_APP_SPECIFIC_FIELDS.SOX_IMPACT,
      AI_APP_SPECIFIC_FIELDS.PERMISSIONS_LINK,
      AI_APP_SPECIFIC_FIELDS.GRC_LINK,
      AI_APP_SPECIFIC_FIELDS.APPLICATION_LINK,
      AI_APP_SPECIFIC_FIELDS.OVERVIEW_CONTENT,
      AI_APP_SPECIFIC_FIELDS.DEMO_VIDEO_URL,
      AI_APP_SPECIFIC_FIELDS.SOP_URL,
      AI_APP_SPECIFIC_FIELDS.SUPPORT_CONTACT_NAME,
      AI_APP_SPECIFIC_FIELDS.SUPPORT_EMAIL,
    ];
    singleValueFields.forEach((field) => {
      if (Array.isArray(appData[field])) {
        appData[field] = appData[field].length > 0 ? appData[field][0] : null;
      }
    });
    return appData;
  };

  const addApplication = async () => {
    // 1. Validate mandatory fields
    let missing = getMissingFields(renderJsonEdited, AI_APP_MANDATORY_FIELDS);
    if (missing.length > 0) {
      setErrorMessage("missing mandatory fields : " + missing.join(", "));
      return;
    }

    // Validate Demo Video URL format
    const demoUrl = renderJsonEdited?.[AI_APP_SPECIFIC_FIELDS.DEMO_VIDEO_URL];
    const demoUrlVal = Array.isArray(demoUrl) ? demoUrl[0] : demoUrl;
    if (demoUrlVal && !demoUrlVal.startsWith("https://broadcast.amazon.com/videos/")) {
      setErrorMessage("Demo Video URL must start with https://broadcast.amazon.com/videos/");
      return;
    }

    setShowLoadingIcon(true);
    setErrorMessage("");

    // 2. Set template_id, created_by, last_updated_by, status
    let appData = JSON.parse(JSON.stringify(renderJsonEdited));
    appData[TEMPLATE_APP.TEMPLATE_ID] = TEMPLATES.AI;
    appData[TEMPLATE_APP.STATUS] = "Active";
    appData.created_by = userId;
    appData.last_updated_by = userId;
    normalizeAppDataForMutation(appData);
    // Set ingestion method after normalization (not in allowlist, only for add)
    if (!appData[AI_APP_SPECIFIC_FIELDS.INGESTION_METHOD]) {
      appData[AI_APP_SPECIFIC_FIELDS.INGESTION_METHOD] = props?.isEditOverride ? "Automated" : "Manual";
    }
    // Upload pending documents to S3 and get clean metadata
    const cleanDocs = await uploadPendingDocuments();
    appData.documents = JSON.stringify(cleanDocs);

    // 3. Create audit log
    let timestamp = new Date().toISOString();
    createAuditLog("APP", "ADD_REQ#" + timestamp, "", JSON.stringify(appData), JSON.stringify(appData), "ADD REQ", userId);

    // 4. Call addAIApp
    addAIApp(appData,
      (response) => {
        setShowLoadingIcon(false);

        // Clear apps listing cache so new app shows immediately
        localStorage.removeItem(GET_APPS_CACHE_KEY);
        // Prevent caching for 60 seconds (read replica lag window)
        localStorage.setItem('appsCacheBypassUntil', String(Date.now() + 60000));

        // Update DynamoDB status to PUBLISHED if this was from unpublished tab
        if (props?.isEditOverride && renderJsonEdited) {
          const appId = response?.data?.addAIApp?.app_id;
          const publishedItem = { ...renderJsonEdited, status: "PUBLISHED" };
          if (appId) publishedItem.app_id = appId;
          // DynamoDB app_owner-index GSI expects a String, not a List
          if (Array.isArray(publishedItem[AI_APP_SPECIFIC_FIELDS.APP_OWNER])) {
            publishedItem[AI_APP_SPECIFIC_FIELDS.APP_OWNER] = publishedItem[AI_APP_SPECIFIC_FIELDS.APP_OWNER][0] || "";
          }
          updateAutomatedApp(
            publishedItem,
            () => {},
            () => {}
          );
          // Pass the DynamoDB item with updated status so Apps.jsx can filter it from the list
          if (props?.onSave) {
            props.onSave(publishedItem);
          }
          // Navigate to the published app page
          if (appId) {
            dispatch(setPendingTrigger(ActionTriggerType.PUBLISH));
            history.push(`/App/${appId}`);
          }
        } else if (props?.onSave) {
          props.onSave(response?.data?.addAIApp);
        } else {
          // Navigate to the newly created app page
          const newAppId = response?.data?.addAIApp?.app_id;
          if (newAppId) {
            dispatch(setPendingTrigger(ActionTriggerType.PUBLISH));
            history.push(`/App/${newAppId}`);
          } else {
            history.push("/AppsLibrary");
          }
        }
      },
      (error) => {
        setShowLoadingIcon(false);
        setErrorMessage(error?.errors?.[0]?.message || "Failed to add application");
      }
    );
  };

  const updateApplication = async () => {
    // 1. Validate mandatory fields
    let missing = getMissingFields(renderJsonEdited, AI_APP_MANDATORY_FIELDS);
    if (missing.length > 0) {
      setErrorMessage("missing mandatory fields : " + missing.join(", "));
      return;
    }

    // Validate Demo Video URL format
    const demoUrl = renderJsonEdited?.[AI_APP_SPECIFIC_FIELDS.DEMO_VIDEO_URL];
    const demoUrlVal = Array.isArray(demoUrl) ? demoUrl[0] : demoUrl;
    if (demoUrlVal && !demoUrlVal.startsWith("https://broadcast.amazon.com/videos/")) {
      setErrorMessage("Demo Video URL must start with https://broadcast.amazon.com/videos/");
      return;
    }

    // 2. Require comments
    if (!updateComments || updateComments.trim() === "") {
      setErrorMessage("Please provide your comments for the update");
      return;
    }

    setShowLoadingIcon(true);
    setErrorMessage("");

    // 3. Set template_id, last_updated_by
    let appData = JSON.parse(JSON.stringify(renderJsonEdited));
    appData[TEMPLATE_APP.TEMPLATE_ID] = TEMPLATES.AI;
    appData.last_updated_by = userId;
    normalizeAppDataForMutation(appData);
    // Upload pending documents to S3 and get clean metadata
    const cleanDocs = await uploadPendingDocuments();
    appData.documents = JSON.stringify(cleanDocs);

    // 4. Create audit log
    let timestamp = new Date().toISOString();
    createAuditLog("APP#" + id, timestamp, updateComments, JSON.stringify(renderJson), JSON.stringify(appData), "UPDATE REQ", userId);

    // 5. Call updateAIApp
    updateAIApp(appData,
      (response) => {
        setShowLoadingIcon(false);
        const updatedApp = response?.data?.updateAIApp;
        if (updatedApp) {
          setRenderJson(updatedApp);
          setRenderJsonEdited(JSON.parse(JSON.stringify(updatedApp)));
        }
        // Refresh appDocuments with clean state (no pending/markedForDeletion flags)
        setAppDocuments(prev => prev.filter(d => !d.markedForDeletion).map(({ file, pending, markedForDeletion, ...rest }) => rest));
        setErrorMessage("");
        setIsEditMode(false);
        setUpdateComments("");
        if (id) loadAuditLogs(id);
        if (props?.onSave) {
          props.onSave(updatedApp);
        }
      },
      (error) => {
        setShowLoadingIcon(false);
        setErrorMessage(error?.errors?.[0]?.message || "Failed to update application");
      }
    );
  };

  const deleteApplication = async () => {
    // 1. Require comments
    if (!updateComments || updateComments.trim() === "") {
      setModalError("Please provide comments with the reason for deletion");
      return;
    }

    setShowLoadingIcon(true);
    setModalError("");

    // 2. Check existing approvals
    try {
      let existingApprovals = await getApprovalsForApp(id);
      if (existingApprovals && existingApprovals.length > 0) {
        setShowLoadingIcon(false);
        setModalMessage("This app is already scheduled for deletion. Approval id : " + existingApprovals[0].workflowInstanceId);
        setDeleteConfirmationModal(false);
        return;
      }
    } catch (err) {
      setShowLoadingIcon(false);
      setModalMessage("Something went wrong. Cannot fetch existing approvals for the app.");
      setDeleteConfirmationModal(false);
      return;
    }

    // 3. Create audit log
    let timestamp = new Date().toISOString();
    createAuditLog("APP#" + id, "DEL_REQ#" + timestamp, updateComments, JSON.stringify(renderJson), "", "DEL REQ", userId);

    // 4. Start WAS approval
    try {
      let approvalResponse = await startWASApproval({
        userAlias: userId,
        stage: currentStage,
        appId: id,
        appName: renderJson?.app_name,
        comments: updateComments,
        approveResponseNeeded: 1
      });

      let workflowInstanceId = approvalResponse?.workflowInstanceId;

      // 5. Delete with approval
      deleteAppWithApproval(id, workflowInstanceId,
        (response) => {
          setShowLoadingIcon(false);
          setDeleteConfirmationModal(false);
          setModalMessage("Deletion request submitted. Approval ID: " + workflowInstanceId);
        },
        (error) => {
          setShowLoadingIcon(false);
          setDeleteConfirmationModal(false);
          setModalMessage("Could not auto-trigger deletion. Approval ID: " + workflowInstanceId + ". Please follow up manually.");
        }
      );
    } catch (err) {
      setShowLoadingIcon(false);
      setDeleteConfirmationModal(false);
      setModalMessage("Could not create approval for deletion. Please try again.");
    }
  };

  // --- Save Unpublished App (uses updateAutomatedApp API) ---

  const handleGoToUnpublished = () => {
    setShowSaveSuccessModal(false);
    if (props?.onSave) {
      props.onSave(savedAppDataRef.current);
    } else {
      history.push('/AppsLibrary?section=unpublished');
    }
  };

  const saveUnpublishedApp = async () => {
    setShowLoadingIcon(true);
    setErrorMessage("");

    let appData = JSON.parse(JSON.stringify(renderJsonEdited || {}));
    appData[TEMPLATE_APP.TEMPLATE_ID] = TEMPLATES.AI;
    appData.last_updated_by = userId;
    // DynamoDB app_owner-index GSI expects a String, not a List
    if (Array.isArray(appData[AI_APP_SPECIFIC_FIELDS.APP_OWNER])) {
      appData[AI_APP_SPECIFIC_FIELDS.APP_OWNER] = appData[AI_APP_SPECIFIC_FIELDS.APP_OWNER][0] || "";
    }
    // Upload pending documents to S3 and include metadata in DynamoDB
    const cleanDocs = await uploadPendingDocuments();
    appData.documents = JSON.stringify(cleanDocs);

    updateAutomatedApp(appData,
      (response) => {
        setShowLoadingIcon(false);
        setShowSaveSuccessModal(true);
        savedAppDataRef.current = appData;
        // Sync local state: remove deleted docs, strip pending flags and file blobs
        setAppDocuments(prev =>
          prev
            .filter(d => !d.markedForDeletion)
            .map(({ file, pending, markedForDeletion, ...rest }) => rest)
        );
        // Clear apps listing cache
        localStorage.removeItem(GET_APPS_CACHE_KEY);
        // Prevent caching for 60 seconds (read replica lag window)
        localStorage.setItem('appsCacheBypassUntil', String(Date.now() + 60000));
      },
      (error) => {
        setShowLoadingIcon(false);
        setErrorMessage(error?.errors?.[0]?.message || "Failed to save application");
      }
    );
  };

  // --- Document Upload/Download ---

  const uploadDocument = async (file) => {
    if (!file) return;
    // Unpublished: use resource_type_resource_id, Published: use app_id, New: use temp path
    let pathBase;
    if (props?.isEditOverride && renderJson?.resource_type && renderJson?.resource_id) {
      pathBase = `ai-apps/unpublished/${renderJson.resource_type}_${renderJson.resource_id}`;
    } else if (id) {
      pathBase = `ai-apps/${id}`;
    } else {
      // New app - use temp path with userId, will be uploaded with app_id after publish
      pathBase = `ai-apps/new/${userId}_${Date.now()}`;
    }
    if (!pathBase) return;
    // Stage file locally - actual S3 upload happens on Publish/Update/Save
    const fileId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const filePath = `${pathBase}/${fileId}_${file.name}`;
    const newDoc = { id: fileId, name: file.name, path: filePath, uploadedAt: new Date().toISOString(), uploadedBy: userId, file: file, pending: true };
    setAppDocuments(prev => [...prev, newDoc]);
  };

  // Actually upload pending files to S3 and delete flagged files - called from publish/update/save flows
  const uploadPendingDocuments = async () => {
    // Upload new files, track successes
    const pendingDocs = appDocuments.filter(d => d.pending && d.file);
    const successfulUploads = new Set();
    for (const doc of pendingDocs) {
      try {
        const contentType = doc.file.type?.split('/')[1] || 'octet-stream';
        await uploadDocumentToVault(contentType, VAULT_SERVICES.AI_APP, { file_path: doc.path }, doc.file);
        successfulUploads.add(doc.id);
      } catch (err) {
        console.error("Error uploading document:", doc.name, err);
      }
    }
    // Delete flagged files from S3
    const deletedDocs = appDocuments.filter(d => d.markedForDeletion);
    for (const doc of deletedDocs) {
      try {
        const contentType = doc.name.split('.').pop() || 'octet-stream';
        await removeDocumentFromVault(contentType, VAULT_SERVICES.AI_APP, { file_path: doc.path });
      } catch (err) {
        console.error("Error deleting document:", doc.name, err);
      }
    }
    // Return clean metadata: exclude deleted items and failed uploads, strip local-only fields
    return appDocuments
      .filter(d => !d.markedForDeletion && (!d.pending || successfulUploads.has(d.id)))
      .map(({ file, pending, markedForDeletion, ...rest }) => rest);
  };

  const downloadDocument = async (doc) => {
    try {
      const contentType = doc.name.split('.').pop() || 'octet-stream';
      const result = await getDocumentFromVault(contentType, VAULT_SERVICES.AI_APP, { file_path: doc.path });
      if (result?.fileUrl) {
        window.open(result.fileUrl, '_blank');
      }
    } catch (err) {
      console.error("Error downloading document:", err);
    }
  };

  const deleteDocument = async (doc) => {
    if (doc.pending) {
      // Not yet uploaded to S3 - just remove from local state
      setAppDocuments(prev => prev.filter(d => d.id !== doc.id));
    } else {
      // Already in S3 - flag for deletion, actual S3 removal on save/update
      setAppDocuments(prev => prev.map(d => d.id === doc.id ? { ...d, markedForDeletion: true } : d));
    }
  };

  // --- Favorites/Featured (Task 7.1) ---

  const handleRatingChange = (rating) => {
    const oldRating = userRating;
    const oldAvg = displayAvgRating;
    const oldCount = displayRatingCount;

    // Optimistic update
    setUserRating(rating);
    if (rating === 0 && oldRating > 0) {
      // Removing rating
      const newCount = Math.max(oldCount - 1, 0);
      const newAvg = newCount > 0 ? ((oldAvg * oldCount) - oldRating) / newCount : 0;
      setDisplayRatingCount(newCount);
      setDisplayAvgRating(newAvg);
      deleteRating(id, "APP", () => {}, () => {
        // Revert on failure
        setUserRating(oldRating);
        setDisplayAvgRating(oldAvg);
        setDisplayRatingCount(oldCount);
      });
    } else {
      // Adding or updating rating
      const newCount = oldRating === 0 ? oldCount + 1 : oldCount;
      const newAvg = oldRating === 0
        ? ((oldAvg * oldCount) + rating) / newCount
        : ((oldAvg * oldCount) - oldRating + rating) / newCount;
      setDisplayRatingCount(newCount);
      setDisplayAvgRating(newAvg);
      submitRating(id, "APP", rating, () => {}, () => {
        // Revert on failure
        setUserRating(oldRating);
        setDisplayAvgRating(oldAvg);
        setDisplayRatingCount(oldCount);
      });
    }
  };

  const markFavorite = async (appId) => {
    setFavDisabled(true);
    await favorite(appId, userId, function onAdded(result) {
      setIsFavorite(true);
    }, function onDeleted(result) {
      setIsFavorite(false);
    }, function onFailure(result) {
    });
    setFavDisabled(false);
  };

  const markFeatured = async (appId) => {
    await featured(appId, function onAdded(result) {
      setFeaturedPressed(true);
    }, function onDeleted(result) {
      setFeaturedPressed(false);
    }, function onFailure(result) {
    });
  };

  // --- Lifecycle ---

  useEffect(() => {
    setInitView();
    return () => {};
  }, [id]);

  useEffect(() => {
    if (props?.renderJson) {
      setRenderJson(props.renderJson);
      setRenderJsonEdited(props.renderJson);
      setDisplayAvgRating(parseFloat(props.renderJson?.[TEMPLATE_APP.RATING_AVG]) || 0);
      setDisplayRatingCount(parseInt(props.renderJson?.[TEMPLATE_APP.RATING_COUNT]) || 0);
      // Load documents from app data
      if (props.renderJson?.documents) {
        try {
          let rawDocs = props.renderJson.documents;
          // Free-form fields from RDS come back as ["json_string"] array
          if (Array.isArray(rawDocs) && rawDocs.length > 0 && typeof rawDocs[0] === 'string') {
            rawDocs = rawDocs[0];
          }
          const docs = typeof rawDocs === 'string' ? JSON.parse(rawDocs) : rawDocs;
          if (Array.isArray(docs)) {
            // Strip local-only flags (pending, markedForDeletion, file) that may have been persisted
            setAppDocuments(docs.map(({ pending, markedForDeletion, file, ...rest }) => rest));
          }
        } catch (e) { console.error("Error parsing documents:", e); }
      }
    }
  }, [props?.renderJson]);

  // Re-evaluate edit/delete permissions when renderJson data becomes available
  useEffect(() => {
    if (renderJson && userId) {
      const ownerVal = renderJson?.[AI_APP_SPECIFIC_FIELDS.APP_OWNER];
      const isOwner = Array.isArray(ownerVal) ? ownerVal.includes(userId) : ownerVal === userId;

      // Check owning LDAP membership
      const owningLdap = renderJson?.[AI_APP_SPECIFIC_FIELDS.OWNING_LDAP];
      const ldapName = Array.isArray(owningLdap) ? owningLdap[0] : owningLdap;
      if (ldapName && ldapName.trim()) {
        isMemberOf(userId, ldapName.trim(), currentStage).then(isLdapMember => {
          setCanEdit(isAiAdminLocal || isOwner || isLdapMember);
          setCanDelete(isAiAdminLocal || isOwner || isLdapMember);
        }).catch(() => {
          setCanEdit(isAiAdminLocal || isOwner);
          setCanDelete(isAiAdminLocal || isOwner);
        });
      } else {
        setCanEdit(isAiAdminLocal || isOwner);
        setCanDelete(isAiAdminLocal || isOwner);
      }
    }
  }, [renderJson, userId, isAiAdminLocal]);

  // Auto-populate Director (L8) and VP (L10) from supervisor chain when filling from unpublished queue
  // Also auto-populate GRC Control Owner with App Builder if not already filled
  useEffect(() => {
    if (!props?.isEditOverride) return;
    if (!renderJsonEdited) return;

    const appOwner = renderJsonEdited?.[AI_APP_SPECIFIC_FIELDS.APP_OWNER];
    const ownerLogin = Array.isArray(appOwner) ? appOwner[0] : appOwner;
    if (!ownerLogin) return;

    const currentDirector = renderJsonEdited?.[AI_APP_SPECIFIC_FIELDS.DIRECTOR];
    const currentVP = renderJsonEdited?.[TEMPLATE_APP.VP];
    const currentGRC = renderJsonEdited?.[TEMPLATE_APP.GRC_CONTROL_OWNER];
    const directorEmpty = !currentDirector || currentDirector.length === 0;
    const vpEmpty = !currentVP || currentVP.length === 0;
    const grcEmpty = !currentGRC || currentGRC.length === 0;

    // Skip if all fields are already filled
    if (!directorEmpty && !vpEmpty && !grcEmpty) return;

    const fetchSupervisors = async () => {
      try {
        let clone = JSON.parse(JSON.stringify(renderJsonEdited));
        let changed = false;

        // Auto-populate GRC Control Owner with App Builder
        if (grcEmpty) {
          clone[TEMPLATE_APP.GRC_CONTROL_OWNER] = ownerLogin;
          changed = true;
        }

        if (directorEmpty) {
          const l8Result = await getSupervisorAtLevel(ownerLogin, 8);
          if (l8Result?.login) {
            clone[AI_APP_SPECIFIC_FIELDS.DIRECTOR] = l8Result.login;
            changed = true;
          }
        }

        if (vpEmpty) {
          const l10Result = await getSupervisorAtLevel(ownerLogin, 10);
          if (l10Result?.login) {
            clone[TEMPLATE_APP.VP] = l10Result.login;
            changed = true;
          }
        }

        if (changed) {
          setRenderJsonEdited(clone);
        }
      } catch (e) {
        console.warn("Failed to auto-populate from supervisor chain:", e);
      }
    };

    fetchSupervisors();
  }, [props?.isEditOverride, renderJsonEdited?.[AI_APP_SPECIFIC_FIELDS.APP_OWNER]]);

  // --- Initialization ---

  const setInitView = async () => {
    if (id) {
      setIsEditMode(props?.isEditOverride || false);
      setIsNewApp(false);
      // Check favorite/featured status for existing apps
      checkIsFavorite(id, userId, (result) => setIsFavorite(result));
      checkIsFeatured(id, (result) => setFeaturedPressed(result));
    } else {
      setIsNewApp(true);
      setIsEditMode(true);
    }
    loadEditableFields();
    if (id) {
      loadAuditLogs(id);
      // Fetch user's rating for this app
      getUserRatings("APP",
        (result) => {
          const ratings = result?.data?.getUserRatings || [];
          const myRating = ratings.find(r => r.entity_id === id);
          if (myRating) setUserRating(myRating.rating);
        },
        () => {}
      );
    }

    let accessPerms = await canAddEditDeleteApplication(userId, TEMPLATES.AI, currentStage);
    const isAdmin = accessPerms?.edit || accessPerms?.delete;
    setIsAiAdminLocal(isAdmin);
    const ownerVal = renderJson?.[AI_APP_SPECIFIC_FIELDS.APP_OWNER];
    const isOwner = Array.isArray(ownerVal) ? ownerVal.includes(userId) : ownerVal === userId;

    // Check if user is member of the owning LDAP
    let isLdapMember = false;
    const owningLdap = renderJson?.[AI_APP_SPECIFIC_FIELDS.OWNING_LDAP];
    const ldapName = Array.isArray(owningLdap) ? owningLdap[0] : owningLdap;
    if (ldapName && ldapName.trim()) {
      try {
        isLdapMember = await isMemberOf(userId, ldapName.trim(), currentStage);
      } catch (e) {
        console.warn("Failed to check LDAP membership for owning LDAP:", e);
      }
    }
    
    // Add: everyone can add AI apps
    setCanAdd(true);
    // Edit/Delete: restricted to admins, owners, and owning LDAP members
    setCanEdit(isAdmin || isOwner || isLdapMember);
    setCanDelete(isAdmin || isOwner || isLdapMember);

    if (!id) {
      setIsNewApp(true);
      if (!isEditMode) setIsEditMode(true);
    }

    setAlertErrorMessage(null);

    setShowLoadingIcon(false);
  };

  const loadEditableFields = async () => {
    let res = await getEditableFields();
    setFieldMetadata(res);
  };

  const loadAuditLogs = async (appId) => {
    setAuditLogsLoading(true);
    try {
      const logs = await getLogsById(`APP#${appId}`);
      // Show BOT entries (confirmed updates) with user comments from matching REQ
      // Show orphan REQ entries (no matching BOT) as pending/failed
      const allLogs = logs || [];
      const botLogs = allLogs.filter(l => l.actor === "BOT");
      const reqLogs = allLogs.filter(l => l.actor !== "BOT");

      const combined = [];
      const matchedReqs = new Set();

      botLogs.forEach(botLog => {
        // Find the closest REQ entry that came just before this BOT entry
        const matchingReq = reqLogs.find((req, idx) => {
          if (matchedReqs.has(idx)) return false;
          const botTs = parseFloat(botLog.last_updated_time_stamp || botLog.sk || 0);
          const reqTs = parseFloat(req.last_updated_time_stamp || req.sk || 0);
          // REQ should be within 30 seconds before BOT
          return botTs >= reqTs && (botTs - reqTs) < 30;
        });

        if (matchingReq) {
          matchedReqs.add(reqLogs.indexOf(matchingReq));
          combined.push({
            ...botLog,
            comments: matchingReq.comments || botLog.comments,
            actor: matchingReq.actor,
            action: botLog.action === "AI APP UPDATED" ? "Update Complete" : botLog.action === "AI APP CREATED" ? "Created" : botLog.action,
          });
        } else {
          combined.push({
            ...botLog,
            action: botLog.action === "AI APP UPDATED" ? "Update Complete" : botLog.action === "AI APP CREATED" ? "Created" : botLog.action,
            actor: "System",
          });
        }
      });

      // Add unmatched REQ entries as "Pending"
      reqLogs.forEach((req, idx) => {
        if (!matchedReqs.has(idx)) {
          combined.push({
            ...req,
            action: req.action === "UPDATE REQ" ? "Update Initiated" : req.action === "ADD REQ" ? "Create Initiated" : req.action,
          });
        }
      });

      // Sort by timestamp descending
      combined.sort((a, b) => {
        const tsA = parseFloat(a.last_updated_time_stamp || a.sk || 0);
        const tsB = parseFloat(b.last_updated_time_stamp || b.sk || 0);
        return tsB - tsA;
      });

      setAuditLogs(combined);
    } catch (e) {
      console.error("Failed to load audit logs", e);
      setAuditLogs([]);
    }
    setAuditLogsLoading(false);
  };

  // --- Render (preserving existing UI layout) ---

  if (showLoadingIcon) {
    return (
      <AppLayout
        content={
          <Box textAlign="center" padding={{ top: "xxxl" }}>
            <Spinner size="large" />
            <Box variant="p" margin={{ top: "s" }}>Loading...</Box>
          </Box>
        }
        breadcrumbs={
          <BreadcrumbGroup
            items={[
              { text: "Application Marketplace", href: "/AppsLibrary" },
              { text: "AI App", href: "/AIApp" },
            ]}
          />
        }
        navigationHide
        toolsHide
      />
    );
  }

  if (alertErrorMessage) {
    return (
      <AppLayout
        content={
          <Box padding={{ top: "l" }}>
            <Alert type="error" header={alertErrorMessage.header}>
              {alertErrorMessage.message}
            </Alert>
          </Box>
        }
        breadcrumbs={
          <BreadcrumbGroup
            items={[
              { text: "Application Marketplace", href: "/AppsLibrary" },
              { text: "AI App", href: "/AIApp" },
            ]}
          />
        }
        navigationHide
        toolsHide
      />
    );
  }

  const inIframe = window.self !== window.top;

  return (
    <AppLayout
      navigation={!props?.isEditOverride && !inIframe ? <HomeSideNav activeHref={`/App/${id}`} appName={renderJson?.app_name} appHref={`/App/${id}`} /> : undefined}
      navigationHide={!!props?.isEditOverride || inIframe}
      content={
        <>
        {props?.isEditOverride && currentStage !== 'prod' && !papiWarningDismissed && (
          <Box margin={{ bottom: "m" }}>
            <Flashbar
              items={[{
                type: "warning",
                dismissible: true,
                dismissLabel: "Dismiss",
                onDismiss: () => setPapiWarningDismissed(true),
                content: "People APIs data in lower environments is not up to date, please make sure if auto populated alias fields are accurate."
              }]}
            />
          </Box>
        )}
        <Container
          header={
            <Header
              variant="h2"
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  {id && !isNewApp && !isEditMode && (
                    <span
                      onClick={() => { if (!favDisabled) markFeatured(id); }}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: "4px",
                        padding: "4px 20px", borderRadius: "20px", height: "32px", boxSizing: "border-box",
                        border: "2px solid #0972d3", color: "#0972d3",
                        fontWeight: 700, fontSize: "14px", cursor: favDisabled ? "not-allowed" : "pointer",
                        backgroundColor: featuredPressed ? "#e8f4fd" : "white"
                      }}
                    >
                      <Icon name={featuredPressed ? "star-filled" : "star"} /> {featuredPressed ? "Featured" : "Feature App"}
                    </span>
                  )}
                  {id && !isNewApp && !isEditMode && (
                    <Button variant="primary" onClick={() => setAddToCPModalVisible(true)}>Add to Workspace</Button>
                  )}
                  {!isEditMode && !isNewApp && canEdit && (
                    <Button onClick={editApplication}>Edit</Button>
                  )}
                  {!isEditMode && !isNewApp && canDelete && (
                    <Button onClick={() => setDeleteConfirmationModal(true)}>Delete</Button>
                  )}
                </SpaceBetween>
              }
            >
              {isNewApp ? "New AI App" : (renderJson?.app_name || "AI App")}
            </Header>
          }
        >
          <SpaceBetween size="l">
            {isNewApp && (
              <BuildingWithAIModal />
            )}
            {alertErrorMessage && (
              <Alert type="error" header={alertErrorMessage.header}>
                {alertErrorMessage.message}
              </Alert>
            )}

            {modalMessage && (
              <Modal
                visible={!!modalMessage}
                onDismiss={() => setModalMessage("")}
                header="Info"
              >
                {modalMessage}
              </Modal>
            )}

            {showSaveSuccessModal && (
              <Modal
                visible={true}
                onDismiss={() => setShowSaveSuccessModal(false)}
                header="Saved"
                footer={
                  <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button onClick={() => setShowSaveSuccessModal(false)}>Stay on this app</Button>
                      <Button variant="primary" onClick={handleGoToUnpublished}>Go to Unpublished Apps</Button>
                    </SpaceBetween>
                  </Box>
                }
              >
                Your changes have been saved successfully.
              </Modal>
            )}

            {/* VIEW MODE - Card-style metadata grid */}
            {!isEditMode && !isNewApp && (
              <SpaceBetween size="l">
                {/* AI-Powered badge */}
                <span className="ai-powered-badge"><Icon name="gen-ai" /> AI-Powered</span>

                {/* Description */}
                <Box variant="p">{displayValue(TEMPLATE_APP.APP_DESCRIPTION)}</Box>

                {/* Rating and Views */}
                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                  <StarRating
                    value={userRating}
                    averageRating={displayAvgRating}
                    ratingCount={displayRatingCount}
                    onChange={handleRatingChange}
                  />
                  <span style={{ fontSize: "13px", display: "inline-flex", alignItems: "center", gap: "4px", color: "#5f6b7a" }}>
                    <span>👁️</span>
                    <span>{getValue(TEMPLATE_APP.VIEW_COUNT) != null ? getValue(TEMPLATE_APP.VIEW_COUNT) : "0"} views</span>
                  </span>
                </div>

                {/* Row 1: Status, App Owner, Director, GRC Control Owner */}
                <ColumnLayout columns={4} variant="text-grid">
                  <div>
                    <Box variant="awsui-key-label">Status</Box>
                    <Box variant="p">
                      {getValue(TEMPLATE_APP.STATUS) === 'Active' ?
                        <span style={{color:"#00802f"}}><Icon name="status-positive" variant="success" />&nbsp;{getValue(TEMPLATE_APP.STATUS)}</span>
                        : getValue(TEMPLATE_APP.STATUS) === 'Deprecated' ?
                        <span style={{color:"#db0000"}}><Icon name="status-negative" variant="error" />&nbsp;{getValue(TEMPLATE_APP.STATUS)}</span>
                        : getValue(TEMPLATE_APP.STATUS) === 'WIP' ?
                        <span style={{color:"#0f141a"}}><Icon name="status-pending" variant="normal" />&nbsp;{getValue(TEMPLATE_APP.STATUS)}</span>
                        : <span style={{color:"#855900"}}><Icon name="status-warning" variant="warning" />&nbsp;No status</span>
                      }
                    </Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">App Builder {" "}
                      <Popover
                        dismissButton={false}
                        position="top"
                        size="small"
                        triggerType="custom"
                        content="The Accounting point of contact responsible for this application. This person will have edit and delete access to the app listing."
                      >
                        <span style={{ cursor: "pointer" }}><Icon name="status-info" variant="link" /></span>
                      </Popover>
                    </Box>
                    <Box variant="p">{displayAlias(AI_APP_SPECIFIC_FIELDS.APP_OWNER)}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Director (L8)</Box>
                    <Box variant="p">{displayAlias(AI_APP_SPECIFIC_FIELDS.DIRECTOR)}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">GRC Control Owner {" "}
                      <Popover
                        dismissButton={false}
                        position="top"
                        size="medium"
                        triggerType="custom"
                        content="ATEAM operated a bi-annual GRC control for all the applications in this repository; This is the person who will confirm, on a bi-annual basis through GRC, that this application's financial relevance and ledger impact have not changed in scope."
                      >
                        <span style={{ cursor: "pointer" }}><Icon name="status-info" variant="link" /></span>
                      </Popover>
                    </Box>
                    <Box variant="p">{displayAlias(TEMPLATE_APP.GRC_CONTROL_OWNER)}</Box>
                  </div>
                </ColumnLayout>

                {/* Row 2: VP, Owning LDAP */}
                <ColumnLayout columns={4} variant="text-grid">
                  <div>
                    <Box variant="awsui-key-label">VP</Box>
                    <Box variant="p">{displayAlias(TEMPLATE_APP.VP)}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Owning LDAP</Box>
                    <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.OWNING_LDAP)}</Box>
                  </div>
                </ColumnLayout>

                {/* Row 3: Created By, Region, Estimated Annual MER, SOX Impact */}
                <ColumnLayout columns={4} variant="text-grid">
                  <div>
                    <Box variant="awsui-key-label">Created By</Box>
                    <Box variant="p">{displayAlias(TEMPLATE_APP.CREATED_BY)}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Region</Box>
                    <Box variant="p">{displayValue(TEMPLATE_APP.REGION)}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Estimated Annual MER</Box>
                    <Box variant="p">{displayValue(TEMPLATE_APP.ANNUAL_MER_SAVINGS)}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">SOX Impact</Box>
                    <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.SOX_IMPACT)}</Box>
                  </div>
                </ColumnLayout>

                {/* Row 4: Organization, Use Case Type, Solution Type, Created */}
                <ColumnLayout columns={4} variant="text-grid">
                  <div>
                    <Box variant="awsui-key-label">Organization</Box>
                    <Box variant="p">{displayValue(TEMPLATE_APP.ORGANIZATION)}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Use Case Type</Box>
                    <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.USE_CASE_TYPE)}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Solution Type</Box>
                    <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.SOLUTION_TYPE)}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Created</Box>
                    <Box variant="p">{getValue(TEMPLATE_APP.CREATE_TIME_STAMP) ? new Date(getValue(TEMPLATE_APP.CREATE_TIME_STAMP) * 1000).toLocaleDateString() : "—"}</Box>
                  </div>
                </ColumnLayout>

                {/* Row 5: Last Modified, App Ingestion Method */}
                <ColumnLayout columns={4} variant="text-grid">
                  <div>
                    <Box variant="awsui-key-label">Last Modified</Box>
                    <Box variant="p">{getValue(TEMPLATE_APP.LAST_UPDATED_TIME_STAMP) ? new Date(getValue(TEMPLATE_APP.LAST_UPDATED_TIME_STAMP) * 1000).toLocaleDateString() : "—"}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">App Ingestion Method</Box>
                    <Box variant="p">{(() => {
                      const val = getValue(AI_APP_SPECIFIC_FIELDS.INGESTION_METHOD);
                      if (Array.isArray(val) && val.length > 0 && val[0]) return val[0];
                      if (val && !Array.isArray(val)) return val;
                      return renderJson ? "Manual" : "—";
                    })()}</Box>
                  </div>
                </ColumnLayout>

                {/* Row 6: Automation Tool, Permissions, Solution URL, Link to GRC */}
                <ColumnLayout columns={4} variant="text-grid">
                  <div>
                    <Box variant="awsui-key-label">Automation Tool</Box>
                    <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.AUTOMATION_TOOL)}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Permissions</Box>
                    <Box variant="p">{displayLabeledLink(AI_APP_SPECIFIC_FIELDS.PERMISSIONS_LINK, "View Permissions")}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Solution URL</Box>
                    <Box variant="p">{displayLabeledLink(AI_APP_SPECIFIC_FIELDS.APPLICATION_LINK, "Open Application")}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Link to GRC</Box>
                    <Box variant="p">{displayLabeledLink(AI_APP_SPECIFIC_FIELDS.GRC_LINK, "View GRC Controls")}</Box>
                  </div>
                </ColumnLayout>

                {/* Tags */}
                <div>
                  <Box variant="awsui-key-label">Tags {" "}
                    <Popover
                      dismissButton={false}
                      position="top"
                      size="small"
                      triggerType="custom"
                      content="Tags help our search return more relevant results so your solution is easier to discover."
                    >
                      <span style={{ cursor: "pointer" }}><Icon name="status-info" variant="link" /></span>
                    </Popover>
                  </Box>
                  <Box padding={{ top: "xxs" }}>
                    <SpaceBetween direction="horizontal" size="xs">
                      {getValue(AI_APP_SPECIFIC_FIELDS.TAGS) && Array.isArray(getValue(AI_APP_SPECIFIC_FIELDS.TAGS)) ?
                        getValue(AI_APP_SPECIFIC_FIELDS.TAGS).map((tag, index) => (
                          <span key={index} className={["card-label-blue", "card-label-grey", "card-label-red"][index % 3]}>{tag}</span>
                        ))
                        : <Box variant="p">—</Box>
                      }
                    </SpaceBetween>
                  </Box>
                </div>
              </SpaceBetween>
            )}

            {/* EDIT MODE - Form fields */}
            {(isEditMode || isNewApp) && (
              <SpaceBetween size="l">

            {/* Application Name */}
            <FormField label={requiredLabel("Application Name")}>
              {isEditable(TEMPLATE_APP.APP_NAME) ? (
                <CustomInput
                  field={TEMPLATE_APP.APP_NAME}
                  placeholder="Enter application name"
                  getValue={getValue}
                  updateValue={updateValue}
                />
              ) : (
                <Box variant="p">{displayValue(TEMPLATE_APP.APP_NAME)}</Box>
              )}
            </FormField>

            {/* Description */}
            <FormField label={requiredLabel("Description")}>
              {isEditable(TEMPLATE_APP.APP_DESCRIPTION) ? (
                <Textarea
                  value={getValue(TEMPLATE_APP.APP_DESCRIPTION) || ""}
                  placeholder="Detailed description of the application"
                  rows={4}
                  onChange={({ detail }) => updateValue(TEMPLATE_APP.APP_DESCRIPTION, detail.value)}
                />
              ) : (
                <Box variant="p">{displayValue(TEMPLATE_APP.APP_DESCRIPTION)}</Box>
              )}
            </FormField>

            {/* Solution URL */}
            <FormField label={requiredLabel("Solution URL")}>
              {isEditable(AI_APP_SPECIFIC_FIELDS.APPLICATION_LINK) ? (
                <CustomInput
                  field={AI_APP_SPECIFIC_FIELDS.APPLICATION_LINK}
                  placeholder="https://..."
                  getValue={getValue}
                  updateValue={updateValue}
                />
              ) : (
                <Box variant="p">{displayLink(AI_APP_SPECIFIC_FIELDS.APPLICATION_LINK)}</Box>
              )}
            </FormField>

            {/* Use Case Type */}
            <FormField label={requiredLabel("Use Case Type")}>
              {isEditable(AI_APP_SPECIFIC_FIELDS.USE_CASE_TYPE) ? (
                <CustomSelect
                  field={AI_APP_SPECIFIC_FIELDS.USE_CASE_TYPE}
                  getValue={getValue}
                  updateValue={updateValue}
                  getOptions={getSelectFieldValues}
                />
              ) : (
                <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.USE_CASE_TYPE)}</Box>
              )}
            </FormField>

            {/* Tags */}
            <FormField label={<>Tags {" "}
              <Popover
                dismissButton={false}
                position="top"
                size="small"
                triggerType="custom"
                content="Tags help our search return more relevant results so your solution is easier to discover."
              >
                <span style={{ cursor: "pointer" }}><Icon name="status-info" variant="link" /></span>
              </Popover>
            </>}>
              {isEditable(AI_APP_SPECIFIC_FIELDS.TAGS) ? (
                <CustomMultiSelect
                  field={AI_APP_SPECIFIC_FIELDS.TAGS}
                  updateValue={updateValue}
                  getValues={getSelectFieldValues}
                  getSelectedOptions={getMultiSelectedOptions}
                />
              ) : (
                <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.TAGS)}</Box>
              )}
            </FormField>

            {/* Solution Type */}
            <FormField
              label={requiredLabel("Solution Type")}
              description={isEditable(AI_APP_SPECIFIC_FIELDS.SOLUTION_TYPE) ? "AI capability classification based on the SOX ML/AI Risk Management Policy. Support: ML/AI provides visuals, exploration, alerts, and other support for human decision makers; the decision is made by humans. Augment: ML/AI generates recommendations and may provide diagnostic analytics for human validation; AI suggests and human decides, or human suggests and machine decides, or both decide together. Automate: Autonomous decision-making using predictions, forecasts, simulations, rules, or optimization; risks managed by guardrails with a human-in-the-loop for exceptional cases." : ""}
            >
              {isEditable(AI_APP_SPECIFIC_FIELDS.SOLUTION_TYPE) ? (
                <CustomSelect
                  field={AI_APP_SPECIFIC_FIELDS.SOLUTION_TYPE}
                  getValue={getValue}
                  updateValue={updateValue}
                  getOptions={getSelectFieldValues}
                />
              ) : (
                <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.SOLUTION_TYPE)}</Box>
              )}
            </FormField>

            <Header variant="h3">Metadata</Header>

            {/* Organization & Automation Tool */}
            <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
              <FormField label={requiredLabel("Organization")}>
                {isEditable(TEMPLATE_APP.ORGANIZATION) ? (
                  <CustomMultiSelect
                    field={TEMPLATE_APP.ORGANIZATION}
                    updateValue={updateValue}
                    getValues={getSelectFieldValues}
                    getSelectedOptions={getMultiSelectedOptions}
                  />
                ) : (
                  <Box variant="p">{displayValue(TEMPLATE_APP.ORGANIZATION)}</Box>
                )}
              </FormField>
              <FormField label={requiredLabel("Automation Tool")}>
                {isEditable(AI_APP_SPECIFIC_FIELDS.AUTOMATION_TOOL) ? (
                  <CustomSelect
                    field={AI_APP_SPECIFIC_FIELDS.AUTOMATION_TOOL}
                    getValue={getValue}
                    updateValue={updateValue}
                    getOptions={getSelectFieldValues}
                  />
                ) : (
                  <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.AUTOMATION_TOOL)}</Box>
                )}
              </FormField>
            </Grid>

            {/* App Builder & Director (L8) */}
            <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
              <FormField label={requiredLabel(<>App Builder {" "}
                <Popover
                  dismissButton={false}
                  position="top"
                  size="small"
                  triggerType="custom"
                  content="The Accounting point of contact responsible for this application. This person will have edit and delete access to the app listing."
                >
                  <span style={{ cursor: "pointer" }}><Icon name="status-info" variant="link" /></span>
                </Popover>
              </>)}>
                {isEditable(AI_APP_SPECIFIC_FIELDS.APP_OWNER) ? (
                  !isNewApp && !props?.isEditOverride && id ? (
                    <Multiselect
                      selectedOptions={(getValue(AI_APP_SPECIFIC_FIELDS.APP_OWNER) || []).map(v => ({label: v, value: v}))}
                      onChange={({ detail }) => {
                        updateValue(AI_APP_SPECIFIC_FIELDS.APP_OWNER, detail.selectedOptions.map(o => o.value));
                      }}
                      options={suggestedLogins}
                      placeholder="Select owners"
                      statusType={aliasesLoading ? "loading" : "finished"}
                      filteringType="manual"
                      loadingText="Loading aliases"
                      empty="No valid alias to recommend for entered text"
                      onLoadItems={({detail}) => { getSuggestedLoginsCaller(detail?.filteringText); }}
                    />
                  ) : (
                    <CustomSelectAlias
                      field={AI_APP_SPECIFIC_FIELDS.APP_OWNER}
                      getValue={getValue}
                      updateValue={updateValue}
                      options={suggestedLogins}
                      getOptions={getSuggestedLoginsCaller}
                      aliasesLoading={aliasesLoading}
                    />
                  )
                ) : (
                  <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.APP_OWNER)}</Box>
                )}
              </FormField>
              <FormField label={requiredLabel("Director (L8)")}>
                {isEditable(AI_APP_SPECIFIC_FIELDS.DIRECTOR) ? (
                  <CustomSelectAlias
                    field={AI_APP_SPECIFIC_FIELDS.DIRECTOR}
                    getValue={getValue}
                    updateValue={updateValue}
                    options={suggestedLogins}
                    getOptions={getSuggestedLoginsCaller}
                    aliasesLoading={aliasesLoading}
                  />
                ) : (
                  <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.DIRECTOR)}</Box>
                )}
              </FormField>
            </Grid>

            {/* GRC Control Owner & VP */}
            <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
              <FormField label={requiredLabel(<>GRC Control Owner {" "}
                <Popover
                  dismissButton={false}
                  position="top"
                  size="medium"
                  triggerType="custom"
                  content="ATEAM operated a bi-annual GRC control for all the applications in this repository; This is the person who will confirm, on a bi-annual basis through GRC, that this application's financial relevance and ledger impact have not changed in scope."
                >
                  <span style={{ cursor: "pointer" }}><Icon name="status-info" variant="link" /></span>
                </Popover>
              </>)}>
                {isEditable(TEMPLATE_APP.GRC_CONTROL_OWNER) ? (
                  <CustomSelectAlias
                    field={TEMPLATE_APP.GRC_CONTROL_OWNER}
                    getValue={getValue}
                    updateValue={updateValue}
                    options={suggestedLogins}
                    getOptions={getSuggestedLoginsCaller}
                    aliasesLoading={aliasesLoading}
                  />
                ) : (
                  <Box variant="p">{displayValue(TEMPLATE_APP.GRC_CONTROL_OWNER)}</Box>
                )}
              </FormField>
              <FormField label={requiredLabel("VP")}>
                {isEditable(TEMPLATE_APP.VP) ? (
                  <Select
                    selectedOption={getValue(TEMPLATE_APP.VP) ? { label: getValue(TEMPLATE_APP.VP), value: getValue(TEMPLATE_APP.VP) } : null}
                    onChange={({ detail }) => updateValue(TEMPLATE_APP.VP, detail.selectedOption.value)}
                    placeholder="Select a VP Alias"
                    options={directReports}
                    loadingText="Loading Aliases..."
                    onLoadItems={() => getDirectReportsCaller('shelleyr')}
                    statusType={directReports?.length <= 0 ? "loading" : "finished"}
                  />
                ) : (
                  <Box variant="p">{displayValue(TEMPLATE_APP.VP)}</Box>
                )}
              </FormField>
            </Grid>

            {/* Owning LDAP */}
            <Grid gridDefinition={[{ colspan: 6 }]}>
              <FormField label="Owning LDAP">
                {isEditable(AI_APP_SPECIFIC_FIELDS.OWNING_LDAP) ? (
                  <Input
                    value={Array.isArray(getValue(AI_APP_SPECIFIC_FIELDS.OWNING_LDAP)) ? getValue(AI_APP_SPECIFIC_FIELDS.OWNING_LDAP)[0] || "" : getValue(AI_APP_SPECIFIC_FIELDS.OWNING_LDAP) || ""}
                    placeholder="e.g., acc-team-ldap"
                    onChange={({ detail }) => updateValue(AI_APP_SPECIFIC_FIELDS.OWNING_LDAP, detail.value ? [detail.value] : [])}
                  />
                ) : (
                  <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.OWNING_LDAP)}</Box>
                )}
              </FormField>
            </Grid>

            {/* Region & Estimated Annual MER (hours) */}
            <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
              <FormField label={requiredLabel("Region")}>
                {isEditable(TEMPLATE_APP.REGION) ? (
                  <CustomMultiSelect
                    field={TEMPLATE_APP.REGION}
                    updateValue={updateValue}
                    getValues={getSelectFieldValues}
                    getSelectedOptions={getMultiSelectedOptions}
                  />
                ) : (
                  <Box variant="p">{displayValue(TEMPLATE_APP.REGION)}</Box>
                )}
              </FormField>
              <FormField label={requiredLabel("Estimated Annual MER (hours)")}>
                {isEditable(TEMPLATE_APP.ANNUAL_MER_SAVINGS) ? (
                  <CustomInput
                    field={TEMPLATE_APP.ANNUAL_MER_SAVINGS}
                    placeholder="e.g., 2,500 hours"
                    getValue={getValue}
                    updateValue={updateValue}
                  />
                ) : (
                  <Box variant="p">{displayValue(TEMPLATE_APP.ANNUAL_MER_SAVINGS)}</Box>
                )}
              </FormField>
            </Grid>

            {/* Permissions Link & SOX Impact */}
            <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
              <FormField label="Permissions Link">
                {isEditable(AI_APP_SPECIFIC_FIELDS.PERMISSIONS_LINK) ? (
                  <CustomInput
                    field={AI_APP_SPECIFIC_FIELDS.PERMISSIONS_LINK}
                    placeholder="URL for requesting access"
                    getValue={getValue}
                    updateValue={updateValue}
                  />
                ) : (
                  <Box variant="p">{displayLink(AI_APP_SPECIFIC_FIELDS.PERMISSIONS_LINK)}</Box>
                )}
              </FormField>
              <FormField label={requiredLabel("SOX Impact")}>
                {isEditable(AI_APP_SPECIFIC_FIELDS.SOX_IMPACT) ? (
                  <CustomSelect
                    field={AI_APP_SPECIFIC_FIELDS.SOX_IMPACT}
                    getValue={getValue}
                    updateValue={updateValue}
                    getOptions={getSelectFieldValues}
                  />
                ) : (
                  <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.SOX_IMPACT)}</Box>
                )}
              </FormField>
            </Grid>

            {/* Link to GRC */}
            <Grid gridDefinition={[{ colspan: 6 }]}>
              <FormField label={isSoxInScope() ? requiredLabel("Link to GRC") : "Link to GRC"} description="Only applicable for SOX in Scope applications.">
                {isEditable(AI_APP_SPECIFIC_FIELDS.GRC_LINK) ? (
                  <CustomInput
                    field={AI_APP_SPECIFIC_FIELDS.GRC_LINK}
                    placeholder="https://grc.example.com/controls/..."
                    getValue={getValue}
                    updateValue={updateValue}
                    disabled={!isSoxInScope()}
                  />
                ) : (
                  <Box variant="p">{displayLink(AI_APP_SPECIFIC_FIELDS.GRC_LINK)}</Box>
                )}
              </FormField>
            </Grid>
            </SpaceBetween>
            )}

            {/* Tabbed sections: Overview, Documentation, Change Log, Support */}
            {(() => {
              const tabsArray = [
                {
                  label: "Overview",
                  id: "overview",
                  content: (
                    <Container header={<Header variant="h3">Overview</Header>}>
                      <SpaceBetween size="l">
                        <FormField 
                          label={isEditable(AI_APP_SPECIFIC_FIELDS.OVERVIEW_CONTENT) ? requiredLabel("Overview Content") : "About this application"}
                          description={isEditable(AI_APP_SPECIFIC_FIELDS.OVERVIEW_CONTENT) ? "Supports Markdown: **bold**, *italic*, ## Heading, - bullet points" : ""}
                        >
                          {isEditable(AI_APP_SPECIFIC_FIELDS.OVERVIEW_CONTENT) ? (
                            <Textarea
                              value={getValue(AI_APP_SPECIFIC_FIELDS.OVERVIEW_CONTENT) || ""}
                              placeholder="Enter detailed overview of the application, key features, benefits, etc."
                              rows={8}
                              onChange={({ detail }) => updateValue(AI_APP_SPECIFIC_FIELDS.OVERVIEW_CONTENT, detail.value)}
                            />
                          ) : (
                            <div className="markdown-content"><ReactMarkdown>{displayValue(AI_APP_SPECIFIC_FIELDS.OVERVIEW_CONTENT)}</ReactMarkdown></div>
                          )}
                        </FormField>
                        <FormField 
                          label={isEditable(AI_APP_SPECIFIC_FIELDS.DEMO_VIDEO_URL) ? "Demo Video URL" : "Demo Video"}
                          description={isEditable(AI_APP_SPECIFIC_FIELDS.DEMO_VIDEO_URL) ? "Must be a Broadcast video URL (https://broadcast.amazon.com/videos/...)" : ""}
                          errorText={isEditable(AI_APP_SPECIFIC_FIELDS.DEMO_VIDEO_URL) && getDemoVideoUrl() && getDemoVideoUrl().trim() !== "" && !getDemoVideoUrl().startsWith("https://broadcast.amazon.com/videos/") ? "URL must start with https://broadcast.amazon.com/videos/" : ""}
                        >
                          {isEditable(AI_APP_SPECIFIC_FIELDS.DEMO_VIDEO_URL) ? (
                            <CustomInput
                              field={AI_APP_SPECIFIC_FIELDS.DEMO_VIDEO_URL}
                              placeholder="https://broadcast.amazon.com/videos/..."
                              getValue={getValue}
                              updateValue={updateValue}
                            />
                          ) : (
                            !getDemoVideoUrl() && <Box variant="p">—</Box>
                          )}
                        </FormField>
                        {!isEditable(AI_APP_SPECIFIC_FIELDS.DEMO_VIDEO_URL) && getDemoVideoUrl() && (
                          <div style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
                            <iframe
                              src={generateEmbedableBroadcastLinkFromVideoLink(getDemoVideoUrl())}
                              style={{ display: "block", width: "100%", height: "56rem", border: "none", borderRadius: "8px" }}
                              allowFullScreen
                              title="Demo Video"
                            />
                          </div>
                        )}
                      </SpaceBetween>
                    </Container>
                  )
                },
                {
                  label: "Documentation",
                  id: "documentation",
                  content: (
                    <Container header={<Header variant="h3">Documentation</Header>}>
                      <SpaceBetween size="l">
                        {/* Uploaded Documents */}
                        <Container
                          header={
                            <Header
                              variant="h4"
                              actions={
                                (id || props?.isEditOverride || isNewApp) && isEditMode ? (
                                  <FileUpload
                                    onChange={({ detail }) => {
                                      if (detail.value?.[0]) {
                                        uploadDocument(detail.value[0]);
                                      }
                                    }}
                                    value={[]}
                                    i18nStrings={{
                                      uploadButtonText: () => "Choose file",
                                      dropzoneText: () => "Drop file to upload",
                                      removeFileAriaLabel: (e) => `Remove file ${e + 1}`,
                                      errorIconAriaLabel: "Error"
                                    }}
                                    showFileSize
                                    multiple={false}
                                  />
                                ) : null
                              }
                            >
                              Uploaded Documents
                            </Header>
                          }
                        >
                          {appDocuments.length > 0 ? (
                            <Table
                              columnDefinitions={[
                                { id: "name", header: "File Name", cell: item => (
                                  <>
                                    {item.markedForDeletion ? (
                                      <span style={{ textDecoration: "line-through", color: "#db0000" }}>{item.name}</span>
                                    ) : item.pending ? (
                                      <span>{item.name}</span>
                                    ) : (
                                      <Link onFollow={() => downloadDocument(item)}>{item.name}</Link>
                                    )}
                                    {item.pending && <span style={{ marginLeft: "8px", fontSize: "11px", color: "#d4760a", fontWeight: 600 }}>⏳ Pending save</span>}
                                    {item.markedForDeletion && <span style={{ marginLeft: "8px", fontSize: "11px", color: "#db0000", fontWeight: 600 }}>🗑️ Pending removal</span>}
                                  </>
                                )},
                                { id: "uploadedBy", header: "Uploaded By", cell: item => item.uploadedBy || "—" },
                                { id: "uploadedAt", header: "Uploaded At", cell: item => item.uploadedAt ? new Date(item.uploadedAt).toLocaleDateString() : "—" },
                                { id: "actions", header: "", cell: item => (
                                  canEdit && isEditMode ? <Button variant="icon" iconName="remove" onClick={() => deleteDocument(item)} /> : null
                                )}
                              ]}
                              items={appDocuments}
                              empty={
                                <Box textAlign="center" color="inherit" padding="s">
                                  <Box variant="p" color="inherit">No documents uploaded.</Box>
                                </Box>
                              }
                            />
                          ) : (
                            <Box textAlign="center" padding="l" color="text-body-secondary">
                              <Box variant="p">No documents uploaded yet.</Box>
                              <Box fontSize="body-s">Upload SOPs, architecture diagrams, runbooks, etc.</Box>
                            </Box>
                          )}
                        </Container>

                        <Header variant="h4">Data Sources</Header>
                        <ExpandableSection
                          variant="navigation"
                          defaultExpanded={true}
                          headerText="Datasets and Source Systems"
                        >
                          {isEditable(TEMPLATE_APP.SOURCE_DATA_TABLES) ?
                            <CustomMultiHybridInputsForSourceTables
                              field={TEMPLATE_APP.SOURCE_DATA_TABLES}
                              updateValue={updateValue}
                              getValues={getValue}
                            />
                            :
                            <Box variant="p">
                              {getValue(TEMPLATE_APP.SOURCE_DATA_TABLES) ? <Table
                                columnDefinitions={[
                                  {
                                    id: "upstream_source",
                                    header: "Upstream Source",
                                    cell: item => item?.upstream_source,
                                    isRowHeader: true
                                  },
                                  {
                                    id: "source_database",
                                    header: "Database",
                                    cell: item => item?.source_database,
                                  },
                                  {
                                    id: "source_schema",
                                    header: "Schema",
                                    cell: item => item?.source_schema,
                                  },
                                  {
                                    id: "source_table",
                                    header: "Table",
                                    cell: item => item?.source_table,
                                  },
                                  {
                                    id: "description",
                                    header: "Description",
                                    cell: item => item?.description,
                                  }
                                ]}
                                items={getValue(TEMPLATE_APP.SOURCE_DATA_TABLES) || []}
                                loadingText="Loading datasets"
                                sortingDisabled
                                empty={
                                  <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
                                    <SpaceBetween size="m">
                                      <b>No datasets</b>
                                    </SpaceBetween>
                                  </Box>
                                }
                              /> : <Box>No datasets added</Box>}
                            </Box>
                          }
                        </ExpandableSection>

                        <Header variant="h4">Available Resources</Header>
                        <FormField label="Process Design Document URL">
                          {isEditable(TEMPLATE_APP.PDD) ? (
                            <CustomInput
                              field={TEMPLATE_APP.PDD}
                              placeholder="https://..."
                              getValue={getValue}
                              updateValue={updateValue}
                            />
                          ) : (
                            <Box variant="p">{displayLink(TEMPLATE_APP.PDD)}</Box>
                          )}
                        </FormField>
                        <FormField label="Standard Operating Procedure (SOP) URL">
                          {isEditable(AI_APP_SPECIFIC_FIELDS.SOP_URL) ? (
                            <CustomInput
                              field={AI_APP_SPECIFIC_FIELDS.SOP_URL}
                              placeholder="https://..."
                              getValue={getValue}
                              updateValue={updateValue}
                            />
                          ) : (
                            <Box variant="p">{displayLink(AI_APP_SPECIFIC_FIELDS.SOP_URL)}</Box>
                          )}
                        </FormField>
                      </SpaceBetween>
                    </Container>
                  )
                },
                {
                  label: "Change Log",
                  id: "changelog",
                  content: (
                    <Container header={<Header variant="h3">Change Log</Header>}>
                      {auditLogsLoading ? (
                        <SkeletonCard count={3} />
                      ) : auditLogs.length > 0 ? (
                        <SpaceBetween size="l">
                        <Cards
                          cardDefinition={{
                            header: item => {
                              const isComplete = item.action?.includes("Complete") || item.action === "Created";
                              const isInitiated = item.action?.includes("Initiated");
                              return (
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                  <span style={{ display: "inline-flex", alignItems: "center" }}>
                                    {isComplete && <Icon name="status-positive" variant="success" />}
                                    {isInitiated && <Icon name="status-pending" variant="warning" />}
                                  </span>
                                  <span style={{ color: isComplete ? "#00802f" : isInitiated ? "#d4760a" : "#0f141a", fontWeight: 700, fontSize: "14px" }}>
                                    {item.action}
                                  </span>
                                  <span style={{ color: "#5f6b7a", fontWeight: 400, fontSize: "14px" }}>by {item.actor || "—"}</span>
                                </div>
                              );
                            },
                            sections: [
                              {
                                id: "comments",
                                content: item => item.comments || "—"
                              },
                              {
                                id: "changes",
                                content: item => {
                                  if (!item.before || !item.after || item.before === "" || item.after === "") return null;
                                  try {
                                    const before = typeof item.before === 'string' ? JSON.parse(item.before) : item.before;
                                    const after = typeof item.after === 'string' ? JSON.parse(item.after) : item.after;
                                    if (!before || !after || typeof before !== 'object' || typeof after !== 'object') return null;

                                    // Normalize a value for comparison: unwrap single-element arrays, sort multi-element arrays, treat null/undefined/[]/'' as equivalent
                                    const normalize = (val) => {
                                      if (val === null || val === undefined || val === '') return null;
                                      if (Array.isArray(val)) {
                                        const filtered = val.filter(v => v !== null && v !== undefined && v !== '');
                                        if (filtered.length === 0) return null;
                                        if (filtered.length === 1) return JSON.stringify(filtered[0]);
                                        // For complex objects in arrays (like documents/source_data_tables), stringify the whole thing sorted
                                        if (typeof filtered[0] === 'object') return JSON.stringify(filtered);
                                        return JSON.stringify([...filtered].sort());
                                      }
                                      return JSON.stringify(val);
                                    };

                                    const allKeys = new Set([...Object.keys(before), ...Object.keys(after)]);
                                    const skipKeys = ['created_time_stamp', 'last_updated_time_stamp', 'created_by', 'last_updated_by', 'template_id', 'app_id', 'rating_avg', 'rating_count', 'view_count', 'status', 'documents', 'source_data_tables', 'app_ingestion_method'];
                                    const changes = [];

                                    // Special display for complex fields
                                    const displayComplex = (key, v) => {
                                      if (v == null || v === '') return "—";
                                      if (key === 'documents') {
                                        try {
                                          let docs = v;
                                          // Could be a string, an array of strings, or an array of objects
                                          if (typeof docs === 'string') docs = JSON.parse(docs);
                                          if (Array.isArray(docs) && docs.length > 0 && typeof docs[0] === 'string') {
                                            // Array wrapping a JSON string: ["[{...}]"]
                                            try { docs = JSON.parse(docs[0]); } catch(e) {}
                                          }
                                          if (Array.isArray(docs)) {
                                            const names = docs.map(d => d?.name || d?.path || '(unnamed)').filter(Boolean);
                                            return names.length > 0 ? names.join(", ") : "—";
                                          }
                                        } catch (e) {}
                                        return "(documents changed)";
                                      }
                                      if (key === 'source_data_tables') {
                                        try {
                                          let tables = v;
                                          if (typeof tables === 'string') tables = JSON.parse(tables);
                                          if (Array.isArray(tables) && tables.length > 0 && typeof tables[0] === 'string') {
                                            try { tables = JSON.parse(tables[0]); } catch(e) {}
                                          }
                                          if (Array.isArray(tables)) {
                                            const names = tables.map(t => t?.table || t?.upstream_source || t?.database || '(entry)').filter(Boolean);
                                            return names.length > 0 ? names.join(", ") : "—";
                                          }
                                        } catch (e) {}
                                        return "(source tables changed)";
                                      }
                                      if (Array.isArray(v)) {
                                        const filtered = v.filter(x => x !== null && x !== undefined && x !== '');
                                        if (filtered.length === 0) return "—";
                                        // If array contains objects, summarize
                                        if (typeof filtered[0] === 'object') return `(${filtered.length} items)`;
                                        return filtered.join(", ");
                                      }
                                      return String(v);
                                    };

                                    allKeys.forEach(key => {
                                      if (skipKeys.includes(key)) return;
                                      const oldNorm = normalize(before[key]);
                                      const newNorm = normalize(after[key]);
                                      if (oldNorm !== newNorm) {
                                        changes.push({ field: key, from: displayComplex(key, before[key]), to: displayComplex(key, after[key]) });
                                      }
                                    });
                                    if (changes.length === 0) return <Box variant="p" color="text-body-secondary"><i>No field changes detected</i></Box>;
                                    return (
                                      <ExpandableSection headerText={`${changes.length} field(s) changed`} variant="footer" defaultExpanded>
                                        <Table
                                          variant="embedded"
                                          columnDefinitions={[
                                            { id: "field", header: "Field", cell: row => row.field },
                                            { id: "from", header: "Previous", cell: row => row.from },
                                            { id: "to", header: "New", cell: row => row.to }
                                          ]}
                                          items={changes}
                                        />
                                      </ExpandableSection>
                                    );
                                  } catch (e) { return <Box variant="p" color="text-body-secondary"><i>Unable to parse changes</i></Box>; }
                                }
                              },
                              {
                                id: "timestamp",
                                content: item => {
                                  const ts = item.last_updated_time_stamp || item.sk;
                                  if (!ts) return "—";
                                  const date = isNaN(ts) ? new Date(ts) : new Date(parseFloat(ts));
                                  if (isNaN(date.getTime())) return ts;
                                  return date.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZoneName: "short" });
                                }
                              }
                            ]
                          }}
                          cardsPerRow={[{ minWidth: 0, cards: 1 }]}
                          items={auditLogs.slice((changeLogPage - 1) * CHANGE_LOG_PAGE_SIZE, changeLogPage * CHANGE_LOG_PAGE_SIZE)}
                          empty={
                            <Box textAlign="center" color="inherit" padding={{ top: "l" }}>
                              <b>No change logs</b>
                            </Box>
                          }
                        />
                        <Box float="right">
                          <Pagination
                            currentPageIndex={changeLogPage}
                            pagesCount={Math.ceil(auditLogs.length / CHANGE_LOG_PAGE_SIZE)}
                            onChange={({ detail }) => setChangeLogPage(detail.currentPageIndex)}
                          />
                        </Box>
                        </SpaceBetween>
                      ) : (
                        <Box variant="p" color="text-body-secondary">
                          Change log will be automatically generated based on application updates after submission.
                        </Box>
                      )}
                    </Container>
                  )
                },
                {
                  label: "Get in Touch",
                  id: "support",
                  content: (
                    <Container header={<Header variant="h3">Get in Touch</Header>}>
                      <SpaceBetween size="l">
                        <FormField label="Point of Contact">
                          {isEditable(AI_APP_SPECIFIC_FIELDS.SUPPORT_CONTACT_NAME) ? (
                            <CustomInput
                              field={AI_APP_SPECIFIC_FIELDS.SUPPORT_CONTACT_NAME}
                              placeholder="Enter point of contact"
                              getValue={getValue}
                              updateValue={updateValue}
                            />
                          ) : (
                            <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.SUPPORT_CONTACT_NAME)}</Box>
                          )}
                        </FormField>
                        <FormField label="Contact Email">
                          {isEditable(AI_APP_SPECIFIC_FIELDS.SUPPORT_EMAIL) ? (
                            <CustomInput
                              field={AI_APP_SPECIFIC_FIELDS.SUPPORT_EMAIL}
                              placeholder="contact@example.com"
                              getValue={getValue}
                              updateValue={updateValue}
                            />
                          ) : (
                            <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.SUPPORT_EMAIL)}</Box>
                          )}
                        </FormField>
                        {!isNewApp && !props?.isEditOverride ? (
                          <>
                            <Header variant="h4">Need Help Scaling?</Header>
                            <Box variant="p">
                              If you need help scaling this solution for your particular use case, you can reach out to ATEAM for assistance.
                            </Box>
                            <Link href="https://form.asana.com/?k=mUhDRpYBgiPWsDqldDg-ng&d=8442528107068" external>Start a conversation</Link>
                          </>
                        ) : null}
                      </SpaceBetween>
                    </Container>
                  )
                }
              ];
              return isEditMode ? (
                <SpaceBetween size="l">
                  {tabsArray.filter(tab => tab.id !== 'changelog').map(tab => <div key={tab.id}>{tab.content}</div>)}
                </SpaceBetween>
              ) : (
                <Tabs tabs={tabsArray} />
              );
            })()}

            {/* Error message at the bottom */}
            {errorMessage && <Alert type="error">{errorMessage}</Alert>}

            {/* Action buttons at the bottom */}
            {isEditMode && (
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button onClick={() => {
                    if (props?.onCancel) {
                      props.onCancel();
                    } else if (isNewApp) {
                      history.push("/AppsLibrary");
                    } else {
                      cancelEdition();
                    }
                  }}>
                    Cancel
                  </Button>
                  {props?.isEditOverride && (
                    <Button onClick={() => saveUnpublishedApp()}>
                      Save
                    </Button>
                  )}
                  <Button variant="primary" onClick={() => (isNewApp || props?.isEditOverride) ? addApplication() : updateApplication()}>
                    {(isNewApp || props?.isEditOverride) ? "Publish" : "Update"}
                  </Button>
                </SpaceBetween>
              </Box>
            )}

            {/* Update Comments - shown in edit mode for existing apps only (not for new apps or unpublished queue) */}
            {isEditMode && !isNewApp && !props?.isEditOverride && (
              <FormField label={requiredLabel("Update Comments")}>
                <Input
                  value={updateComments}
                  placeholder="Describe what changed and why"
                  onChange={({ detail }) => setUpdateComments(detail.value)}
                />
              </FormField>
            )}
          </SpaceBetween>
        </Container>

        {/* Delete Confirmation Modal (Task 8.2) */}
        {deleteConfirmationModal && (
          <Modal
            visible={deleteConfirmationModal}
            onDismiss={() => {
              setDeleteConfirmationModal(false);
              setDeleteAppName("");
              setUpdateComments("");
              setModalError("");
            }}
            header="Confirm Deletion"
            footer={
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button onClick={() => {
                    setDeleteConfirmationModal(false);
                    setDeleteAppName("");
                    setUpdateComments("");
                    setModalError("");
                  }}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    disabled={deleteAppName !== renderJson?.app_name}
                    onClick={deleteApplication}
                  >
                    Confirm Delete
                  </Button>
                </SpaceBetween>
              </Box>
            }
          >
            <SpaceBetween size="m">
              <Box>
                To confirm deletion, type the application name: <Box variant="strong">{renderJson?.app_name}</Box>
              </Box>
              <FormField label="Application Name">
                <Input
                  value={deleteAppName}
                  placeholder="Type application name to confirm"
                  onChange={({ detail }) => setDeleteAppName(detail.value)}
                />
              </FormField>
              <FormField label={requiredLabel("Reason for Deletion")}>
                <Input
                  value={updateComments}
                  placeholder="Provide reason for deletion"
                  onChange={({ detail }) => setUpdateComments(detail.value)}
                />
              </FormField>
              {modalError && <Alert type="error">{modalError}</Alert>}
            </SpaceBetween>
          </Modal>
        )}

        {/* Add to Close Portal Modal */}
        {addToCPModalVisible && (
          <AddToCPModal
            visible={addToCPModalVisible}
            onDismiss={() => setAddToCPModalVisible(false)}
            resourceURL={`${location.pathname}${location.search}`}
          />
        )}
        </>
      }
      breadcrumbs={
        <BreadcrumbGroup
          items={[
            { text: "Application Marketplace", href: "/AppsLibrary" },
            { text: isNewApp ? "New AI App" : (renderJson?.app_name || "AI App"), href: "/AIApp" },
          ]}
        />
      }
      toolsHide
    />
  );
}

export default AIAppPage;
